import { ITaskChainItem, ITaskChainRecipient } from 'app/models/Tasks/TaskChain';
import { ICoverageDetails } from 'app/models/UserCoverage/UserCoverage';

/** Distinct covered employee ids from active Cosmos coverage. */
export function getCoveredEmployeeIdsSet(
  activeCoverage: ICoverageDetails[] | undefined | null,
): Set<number> {
  const set = new Set<number>();
  (activeCoverage ?? []).forEach(c => {
    if (c.coveredEmployeeId != null && c.coveredEmployeeId > 0) {
      set.add(c.coveredEmployeeId);
    }
  });
  return set;
}

/** True when the current user actively covers the task sender (Cosmos), independent of GetTaskChain flags. */
export function isUserCoveringTaskSender(
  task: ITaskChainItem,
  activeCoverage: ICoverageDetails[] | undefined | null,
): boolean {
  const senderId = task.sender?.employeeId;
  if (senderId == null || senderId <= 0) return false;
  return getCoveredEmployeeIdsSet(activeCoverage).has(senderId);
}

/**
 * Recipients the user may act for using Redux active coverage: covered employees who appear on the task.
 * Order matches AC: coverer (self) first if To/CC, then covered names sorted by first name.
 */
export function getRecipientRowsForClientCoverage(
  task: ITaskChainItem,
  currentEmployeeId: number | undefined,
  activeCoverage: ICoverageDetails[] | undefined | null,
): ITaskChainRecipient[] {
  const coveredIds = getCoveredEmployeeIdsSet(activeCoverage);
  if (coveredIds.size === 0) return [];

  const recipients = task.recipients || [];
  const coveredRecipients = recipients.filter(r => coveredIds.has(r.recipientId));
  if (coveredRecipients.length === 0) return [];

  const isCovererRecipient =
    currentEmployeeId != null &&
    currentEmployeeId > 0 &&
    recipients.some(r => r.recipientId === currentEmployeeId);

  const sortedCovered = [...coveredRecipients].sort((a, b) => a.firstName.localeCompare(b.firstName));

  if (isCovererRecipient && currentEmployeeId != null) {
    const selfRow = recipients.find(r => r.recipientId === currentEmployeeId);
    if (!selfRow) return sortedCovered;
    const withoutSelf = sortedCovered.filter(r => r.recipientId !== selfRow.recipientId);
    return [selfRow, ...withoutSelf];
  }

  return sortedCovered;
}

/**
 * AMIE Classic / API path: single coveredEmployee on the task chain payload.
 */
function getOnBehalfRecipientRowsFromApi(
  task: ITaskChainItem,
  currentEmployeeId?: number,
): ITaskChainRecipient[] {
  if (!task.isCoverage || task.coveredEmployee?.id == null) return [];

  const coveredId = task.coveredEmployee.id;
  const isCovererRecipient =
    currentEmployeeId != null &&
    currentEmployeeId > 0 &&
    task.recipients?.some(r => r.recipientId === currentEmployeeId);

  const coveredMatch = (task.recipients || []).filter(r => r.recipientId === coveredId);
  const sortedCovered = [...coveredMatch].sort((a, b) => a.firstName.localeCompare(b.firstName));

  if (isCovererRecipient && currentEmployeeId != null) {
    const selfRow = task.recipients!.find(r => r.recipientId === currentEmployeeId);
    if (!selfRow) return sortedCovered;
    const withoutSelf = sortedCovered.filter(r => r.recipientId !== selfRow.recipientId);
    return [selfRow, ...withoutSelf];
  }

  return sortedCovered;
}

/** Prefer client-derived rows when non-empty; otherwise use API coverage fields if covered employee is still active. */
export function getOnBehalfRecipientRowsUnified(
  task: ITaskChainItem,
  currentEmployeeId: number | undefined,
  activeCoverage: ICoverageDetails[] | undefined | null,
): ITaskChainRecipient[] {
  const clientRows = getRecipientRowsForClientCoverage(task, currentEmployeeId, activeCoverage);
  if (clientRows.length > 0) return clientRows;
  const coveredId = task.coveredEmployee?.id;
  if (coveredId == null || coveredId <= 0 || !getCoveredEmployeeIdsSet(activeCoverage).has(coveredId)) {
    return [];
  }
  return getOnBehalfRecipientRowsFromApi(task, currentEmployeeId);
}

export function userCanEditViaClientCoverage(
  task: ITaskChainItem,
  currentEmployeeId: number | undefined,
  activeCoverage: ICoverageDetails[] | undefined | null,
): boolean {
  return getRecipientRowsForClientCoverage(task, currentEmployeeId, activeCoverage).length > 0;
}

/**
 * Loop-out recipient: signed-in user first, then first on-behalf row that is eligible to loop out.
 */
export function getLoopOutRecipientUnified(
  task: ITaskChainItem,
  currentUserEmployeeId: number | undefined,
  activeCoverage: ICoverageDetails[] | undefined | null,
): ITaskChainRecipient | null {
  if (!task?.recipients?.length) return null;

  const eligibleRecipients = task.recipients.filter(recipient => recipient.canLoopOut);
  if (!eligibleRecipients.length) return null;

  if (currentUserEmployeeId) {
    const currentUserRecipient = eligibleRecipients.find(
      recipient => recipient.recipientId === currentUserEmployeeId,
    );
    if (currentUserRecipient) return currentUserRecipient;
  }

  const unified = getOnBehalfRecipientRowsUnified(task, currentUserEmployeeId, activeCoverage);
  for (const row of unified) {
    const found = eligibleRecipients.find(r => r.recipientId === row.recipientId);
    if (found) return found;
  }

  return null;
}
